import React from 'react'
import clsx from 'clsx'
import moment from 'moment'
import { Tabs, Tab } from '@mui/material'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'
import { AddCircle, ClipboardTick, CloseCircle, Hierarchy } from 'iconsax-react'
import { useTheme } from '@mui/material'

import BaseModal from '../BaseModal2'
import EndControls from '../EndControls'
import TaskDetails from './TaskDetails'
import TextField from '../../../atoms/TextField'
import FileSection from './FileSection'
import HeaderButtons from './HeaderButtons'
import Button from '../../../atoms/Button2'
import TaskArrows from '../../TaskArrows'
import Tooltip from '../../../atoms/Tooltip'
import TaskMetaSection from './TaskMetaSection'
import TaskTagsSection from './TaskTagsSection'
import Info from '../../../atoms/Info'
import Editor, {
  EMPTY_SINGLE_PARAGRAPH,
  isEmpty
} from '../../../organisms/LexicalEditor'
import ThunderIcon from '../../../organisms/ChatWindowV2/ChatBox/InputControls/Icons/ThunderIcon'
import ButtonMenu from '../../ButtonMenu'
import TaskNote from '@SonarCX/components/molecules/TaskNote'
import EmailSection from './EmailSection'

import useAccess, { PERMS } from '@SonarCX/hooks/useAccess'
import useUserPermissions from '@SonarCX/hooks/permissions/useUserPermissions'
import { searchUser } from '@SonarCX/lib/api'
import { uniqueArray, formatDateRelative } from '@SonarCX/utilities'
import { useAgent } from '@SonarCX/providers/AgentProvider'
import { fetchTags, selectTag } from '@SonarCX/slices/tag'
import { fetchTypes, selectTaskType } from '@SonarCX/slices/taskType'
import { selectCategory, fetchCategories } from '@SonarCX/slices/category'
import { useUserAPI } from '@SonarCX/providers/UserAPIProvider'
import { useTaskAPI } from '@SonarCX/providers/TaskAPIProvider'
import { useAreYouSure } from '@SonarCX/providers/AreYouSureProvider'
import { useBreakpoint } from '@SonarCX/providers/BreakpointProvider'
import { useTaskList } from '@SonarCX/providers/TaskListProvider'
import { fetchStatus, selectTaskStatus } from '@SonarCX/slices/taskStatus'
import { fetchPriorities, selectTaskPriority } from '@SonarCX/slices/priority'
import { saveMeta } from '@SonarCX/slices/taskMetaCache'
import { fetchTeams, selectTeams } from '@SonarCX/slices/teamsSlice'
import { AddErrorAlert, AddSuccessAlert } from '@SonarCX/slices/alert'
import { useEnvironment } from '@SonarCX/providers/EnvironmentProvider'

import useStyles from './styles/TaskModal'
import { useSupabase } from '@SonarCX/providers/SupabaseProvider'

export const QUICK_CDD_SUBTASKS = [
  'Verify if customer payment history is current',
  'Prepare Email Draft for Attorney',
  'Send an Email to Attorney',
  'Identify if there are additional charges and collected',
  'Send Payment to Attorney',
  'Receive Ticket result From Attorney and Notify Customer'
]

const ATTACH_FILE = 'Attach a file'
const ATTACH_AUDIO = 'Attach voice note'

const formatTaskForModal = task => ({
  ...task,
  date: task.due_date,
  section: task.category_id,
  client: task.concerning_user_id,
  primary: task.assignee_ids,
  watchers: task.watcher_ids,
  type: task.type_id,
  status: task.status_id,
  priority: task.priority_id,
  tags: task.tags ?? [],
  content: task.details,
  updated_at: task?.updated_at,
  created_at: task?.created_at,
  // Copy media and clear ids to prevent duplicate file uploads
  file: task?.media ?? [],
  media_ids: []
})

const MODAL_ID = 'task-modal'
const DESCRIPTION = 0
const NOTES = 1
const EMAILS = 2

const TaskModal = ({
  open,
  onClose = () => { },
  session = null,
  onTaskCreated = task => { },
  superVals = null,
  disableTaskCreation,
  subtasks,
  taskId,
  setEditSubtasks = () => { },
  editSubtasks,
  media,
  updateSuperVals,
  setSubTask,
  setModalOpen,
  setTaskOpen,
  setSubTaskOpen,
  task,
  user,
  canEdit,
  canUpdate,
  canDelete,
  canViewTask,
  canClose,
  viewAfterCreate = true,
  selectedCategory,
  assignSelf,
  assignClient,
  singleCreate = false,
  assignVals = null,
  ...props
}) => {
  const theme = useTheme()
  const dispatch = useAppDispatch()

  const {
    statuses,
    loaded: taskStatusLoaded,
    loading: taskStatusLoading
  } = useAppSelector(selectTaskStatus)
  const { loaded: tagsLoaded, loading: tagsLoading } = useAppSelector(selectTag)
  const {
    priorities,
    loaded: prioritiesLoaded,
    loading: prioritiesLoading
  } = useAppSelector(selectTaskPriority)
  const {
    typesValues: types,
    loaded: typesLoaded,
    loading: typesLoading
  } = useAppSelector(selectTaskType)
  const {
    categoriesValues: categories,
    loaded: categoriesLoaded,
    loading: categoriesLoading
  } = useAppSelector(selectCategory)
  const {
    teams,
    loaded: teamsLoaded,
    loading: teamsLoading
  } = useAppSelector(selectTeams)
  const { taskCategoryRules: { applyTaskCategoryRules } } = useSupabase()

  const { currentUser } = useAgent()
  const { env } = useEnvironment()
  const { getUsers, uploadUser } = useUserAPI()
  const {
    setAction: setCloseAction,
    setReason: setCloseReason,
    setTitle,
    setSubtext,
    setV2
  } = useAreYouSure()
  const {
    handleTaskUpsert,
    addSubtasks,
    deleteSingleTask,
    tasks,
    startRecording,
    stopRecording,
    isRecording,
    inputFileRef
  } = useTaskAPI()
  const hasPermission = useAccess()
  const { hasBasic: canSearchUser } = useUserPermissions()
  const { down451, up450, up1024 } = useBreakpoint()
  const { pushFrame, popFrame } = useTaskList()

  const [pending, setPending] = React.useState([])
  const [changed, setChanged] = React.useState(false)
  const [meta, setMeta] = React.useState([])
  const [resetMeta, setResetMeta] = React.useState(false)
  const [showInput, setShowInput] = React.useState(false)
  const [subtaskTitle, setSubtaskTitle] = React.useState('')
  const [existingSubtasks, setExistingSubtasks] = React.useState([])
  const [showCompleteButton, setShowCompleteButton] = React.useState(true)
  const [disable, setDisable] = React.useState(true)
  const [clientDetails, setClientDetails] = React.useState(null)
  const [expanded, setExpanded] = React.useState(false)
  const [loadAdd, setLoadAdd] = React.useState(null)
  const [tab, setTab] = React.useState(DESCRIPTION)

  const isCreatingTask = !disableTaskCreation

  const classes = useStyles({ canEdit, smallMedia: down451, isCreatingTask })

  const ticketIcon = () => (
    <ClipboardTick variant='Outline' className={classes.buttonIcon} />
  )

  const startingVals = {
    due_date: moment(),
    title: '',
    section: '',
    client: '',
    primary: [],
    watchers: [],
    type: '',
    status: '',
    priority: '',
    tags: [],
    content: EMPTY_SINGLE_PARAGRAPH,
    file: null,
    subtasks: [],
    parent_name: '',
    updated_at: null,
    created_at: null,
    closed_at: null,
    team_id: null,
    owner_id: null,
    completed_at: null
  }
  const [values, setValues] = React.useState(startingVals)
  const [newUserName, setNewUserName] = React.useState(null)
  const topRef = React.useRef()

  const isCddEnv = React.useMemo(() => env?.name.includes('CDD'), [env?.name])
  const isCreatingCddTask = React.useMemo(
    () => isCddEnv && isCreatingTask,
    [isCddEnv, isCreatingTask]
  )
  const hasAllSubtasksClosed = React.useMemo(() => {
    if (!subtasks?.length) {
      return true
    }

    return subtasks.reduce((acc, cur) => acc && Boolean(cur.closed_at), true)
  }, [subtasks])

  const isSubTask = React.useMemo(
    () => Boolean(values.parent_name),
    [values.parent_name]
  )

  // Get and store agents from user provider
  const agents = React.useMemo(
    () =>
      // Remove extra keys
      Object.values(getUsers(false)).map(({ phone, email, ...rest }) => {
        const mapped = {}

        // Set empty strings to nulls
        Object.entries(rest).forEach(([key, val]) => {
          mapped[key] = val

          if (!val) {
            mapped[key] = null
          }
        })

        return mapped
      }),
    [getUsers]
  )

  const canComplete = React.useMemo(() => {
    if (!subtasks) {
      return true
    }

    return subtasks.reduce((acc, cur) => acc && Boolean(cur.completed_at), true)
  }, [subtasks])

  const canAddClient = React.useMemo(() => {
    if (hasPermission([PERMS.ContactsAudit, PERMS.ContactsOwn])) {
      return true
    }

    return false
  }, [hasPermission])

  // Task is fully done if the status is complete
  const isComplete = React.useMemo(() => {
    if (!values?.status) {
      return false
    }

    const fullStatus = statuses.find(status => status.id === values.status)
    if (!fullStatus) {
      return false
    }

    return fullStatus.base_status === 'completed'
  }, [values, statuses])

  const detailsMenuOpts = React.useMemo(() => [ATTACH_FILE, ATTACH_AUDIO], [])

  const onRecordClick = React.useCallback(() => {
    if (isRecording) {
      stopRecording()
    } else {
      startRecording()
    }
  }, [isRecording, stopRecording, startRecording])

  const handleAttachFileClick = React.useCallback(() => {
    inputFileRef?.current?.click()
  }, [inputFileRef])

  const handleDetailsMenuSelect = React.useCallback(
    name => {
      if (name === ATTACH_FILE) {
        handleAttachFileClick()
      } else if (name === ATTACH_AUDIO) {
        onRecordClick()
      }
    },
    [handleAttachFileClick, onRecordClick]
  )

  React.useEffect(() => {
    if (!taskStatusLoaded && !taskStatusLoading) {
      dispatch(fetchStatus())
    }
  }, [taskStatusLoaded, taskStatusLoading, dispatch])

  React.useEffect(() => {
    if (!tagsLoaded && !tagsLoading) {
      dispatch(fetchTags())
    }
  }, [tagsLoaded, tagsLoading, dispatch])

  React.useEffect(() => {
    if (!prioritiesLoaded && !prioritiesLoading) {
      dispatch(fetchPriorities())
    }
  }, [prioritiesLoaded, prioritiesLoading, dispatch])

  React.useEffect(() => {
    if (!typesLoaded && !typesLoading) {
      dispatch(fetchTypes())
    }
  }, [typesLoaded, typesLoading, dispatch])

  React.useEffect(() => {
    if (!categoriesLoaded && !categoriesLoading) {
      dispatch(fetchCategories())
    }
  }, [categoriesLoaded, categoriesLoading, dispatch])

  React.useEffect(() => {
    if (!teamsLoaded && !teamsLoading) {
      dispatch(fetchTeams())
    }
  }, [teamsLoaded, teamsLoading, dispatch])

  // Toggle mark complete button visibility
  React.useEffect(() => {
    const status = statuses.find(status => status.id === values.status)
    if (status) {
      setShowCompleteButton(status.base_status !== 'completed')
    }
  }, [statuses, values.status])

  // Copy set values from parent
  React.useEffect(() => {
    if (!superVals) {
      return
    }

    // look for parent_id to display
    const found = tasks[taskId]

    let parent_name = null
    if (!found) {
      return
    }

    const formatted = formatTaskForModal(found)

    if (found.parent_id) {
      const sub = tasks[found.parent_id]
      parent_name = sub?.title
    }

    if (found.parent_id === found.id) {
      parent_name = ''
    }

    setValues(vals => {
      const newVals = { ...vals }
      Object.entries(formatted).forEach(([prop, val]) => {
        newVals[prop] = val
      })

      newVals.parent_name = parent_name
      newVals.subtasks = []

      return newVals
    })
  }, [superVals, taskId, values.parent_name, tasks])

  // update list with existing subtasks
  React.useEffect(() => {
    if (subtasks) {
      const list = []
      subtasks.forEach(sub => {
        if (sub.id !== sub.parent_id) {
          list.push(sub)
        }
      })
      setExistingSubtasks(list)
    }
  }, [subtasks])

  // Update types
  React.useEffect(() => {
    if (types.length > 0) {
      setValues(vals => ({ ...vals, type: types[0]?.id }))
    }
  }, [types])

  // Update statuses
  React.useEffect(() => {
    if (statuses.length > 0) {
      const systemOpen = statuses.find(
        status => status?.system && status.base_status === 'open'
      )

      //we *should* have a system === true and base_status open by default, in case we don't added this fallbacks
      const labelOpen = statuses.find(
        status =>
          status.name?.toLowerCase() === 'open' &&
          status?.base_status === 'open'
      )
      const allOpen = statuses.filter(status => status?.base_status === 'open')
      setValues(vals => ({
        ...vals,
        status:
          systemOpen?.id ?? labelOpen?.id ?? allOpen[0]?.id ?? statuses[0]?.id
      }))
    }
  }, [statuses])

  // Update teams
  React.useEffect(() => {
    if (teams.length > 0) {
      const user = currentUser()
      const myTeams = teams.filter(team =>
        user?.teams.some(uTeam => uTeam.id === team.id)
      )
      setValues(vals => ({
        ...vals,
        team_id: myTeams[0]?.id
      }))
    }
  }, [teams, currentUser])

  // Update categories
  React.useEffect(() => {
    if (categories.length > 0 && selectedCategory && open) {
      setValues(vals => ({ ...vals, section: selectedCategory }))
    }
  }, [categories, selectedCategory, open])

  // Adds self to assignees
  React.useEffect(() => {
    const user = currentUser()
    if (open && assignSelf && user) {
      setValues(vals => ({
        ...vals,
        primary: uniqueArray([user.id, ...vals.primary]).filter(Boolean)
      }))
    }
  }, [open, assignSelf, currentUser])

  // Assign client
  React.useEffect(() => {
    if (open && assignClient) {
      setValues(vals => ({ ...vals, client: assignClient }))
    }
  }, [open, assignClient])

  // Assign values from parent
  React.useEffect(() => {
    if (open && assignVals) {
      setValues(vals => ({ ...vals, ...assignVals }))
    }
  }, [open, assignVals])

  // Set changed to false on first render
  React.useEffect(() => {
    setChanged(false)
  }, [])

  const handleChange = prop => e => {
    let val
    if (prop === 'due_date') {
      val = e.toDate()
    } else if (prop === 'content') {
      val = e
    } else if (prop === 'file') {
      val = e
    } else if (prop === 'watchers') {
      let editWatchers = []
      if (e.target.action === 'clear') {
        editWatchers = [...values.watchers]
        const index = editWatchers.indexOf(e.target.value)
        editWatchers.splice(index, 1)
        val = editWatchers
      } else {
        val = editWatchers = [...values.watchers, e.target.value]
      }
    } else if (prop === 'primary') {
      let ids = []
      if (e.target.action === 'clear') {
        ids = [...values.primary]
        const index = ids.indexOf(e.target.value)
        ids.splice(index, 1)
        val = ids
      } else {
        if (
          values.primary_assignee_id &&
          values.primary_assignee_id === e.target.value
        ) {
          dispatch(
            AddErrorAlert({
              err: {
                message: 'Cannot add task lead as additional assignee'
              }
            })
          )
          return
        } else {
          val = ids = [...values.primary, e.target.value]
        }
      }
    } else {
      val = e.target.value
    }

    setValues(vals => ({ ...vals, [prop]: val }))
    if (prop !== 'content' || (!isEmpty(val) && val.trim().length !== 0)) {
      setChanged(true)
    }
  }

  // enabling create button when title, desc and assignee are filled
  React.useEffect(() => {
    if (values?.title) {
      setDisable(false)
    } else {
      setDisable(true)
    }
  }, [values])

  // submitting subtasks or tags where onChange event is one character per time
  const handleFields = (e, field, index) => {
    let val
    // submitting subtasks and tags on enter
    if (e.keyCode === 13) {
      if (field === 'subtasks') {
        if (!subtaskTitle.length) {
          return
        }
        val = [...values[field], subtaskTitle]
        setSubtaskTitle('')
      } else if (field === 'tags') {
        val = [...values[field], e.target.value]
      }
      sendValues(val, field)
      // deleting tags and subtasks
    } else if (index >= 0) {
      let arr = [...values[field]]
      arr.splice(index, 1)
      sendValues(arr, field)
    }

    setChanged(true)
  }

  const sendValues = (val, field) => {
    setValues(vals => ({ ...vals, [field]: val }))
    setChanged(true)
    if (field === 'client') {
      setNewUserName(null)
    }
  }

  const addCddQuickTasks = React.useCallback(() => {
    const field = 'subtasks'
    sendValues(
      Array.from(new Set([...values[field], ...QUICK_CDD_SUBTASKS])),
      field
    )
    dispatch(AddSuccessAlert('Subtasks added'))
  }, [values, dispatch])

  React.useEffect(() => {
    const doLoadAdd = async () => {
      const { data, id, subtasks } = loadAdd
      await addSubtasks(data, id, subtasks, isCreatingCddTask)
      setLoadAdd(null)
    }
    if (loadAdd) {
      doLoadAdd()
    }
    //eslint-disable-next-line
  }, [loadAdd])

  React.useEffect(() => {
    if (user?.id) {
      setClientDetails(user)
    }
  }, [user])

  const scrollToTop = React.useCallback(() => {
    if (topRef.current) {
      topRef.current.scrollIntoView(false)
    }
  }, [])

  // sets an array of existing subtasks to remove on submit
  const pendingDelete = task => {
    if (pending.includes(task.id)) {
      let vals = [...pending]
      let index = pending.indexOf(task.id)
      vals.splice(index, 1)
      setPending(vals)
    } else {
      setPending(pending => [...pending, task.id])
    }
  }

  // clicking on trash can to delete in modal
  const removeTask = React.useCallback(() => {
    try {
      deleteSingleTask(taskId)
      dispatch(AddSuccessAlert('Task deleted'))

      onClose()
    } catch (err) {
      console.error('Error deleting task', err)
    }
  }, [dispatch, deleteSingleTask, onClose, taskId])

  const goToParentTask = () => {
    const action = () => {
      popFrame()
      setTaskOpen(values.parent_id)
    }

    if (changed) {
      setCloseAction(action)
    } else {
      action()
    }
  }

  const handleClose = () => {
    // resets subtasks values and pending delete on close
    setPending([])

    const action = () => {
      onClose(values)
      setSubtaskTitle('')

      // might be assigning tasks as ids
      const { subtasks, ...rest } = superVals ?? {}
      setValues(Object.assign(startingVals, rest))

      setChanged(false)
    }

    if (changed) {
      setV2(true)
      setCloseReason('Are you sure you want to close this modal?')
      setTitle('Are you sure?')
      setSubtext('Continuing will discard those changes')
      setCloseAction(action)
    } else {
      action()
    }
  }

  // Trigger meta section to reset after save
  React.useEffect(() => {
    if (resetMeta) {
      setResetMeta(false)
    }
  }, [resetMeta])

  // Handles saving the meta for this task
  const handleTaskCreated = React.useCallback(
    async (...args) => {
      const task = args[0]
      if (task) {
        dispatch(saveMeta(task.id, meta))
      }
      setResetMeta(true)
      await onTaskCreated(...args)
    },
    [dispatch, meta, onTaskCreated]
  )
  const markComplete = async status => {
    await handleTaskUpsert(
      { ...values, status: status.id },
      disableTaskCreation,
      session,
      newUserName,
      clientDetails,
      taskId,
      handleTaskCreated,
      pending,
      setValues,
      setPending,
      setEditSubtasks,
      editSubtasks,
      setChanged,
      () => { },
      startingVals,
      setLoadAdd,
      viewAfterCreate,
      true
    )
    onClose()
  }

  const subtaskRedirect = React.useCallback(
    task => {
      // delete task from pending delete list
      setPending(pending => pending.filter(id => id !== task.id))
      setTaskOpen(task.id)
      pushFrame(
        existingSubtasks.map(t => t.id),
        task.id
      )
    },
    [setTaskOpen, pushFrame, existingSubtasks]
  )

  const handleAddAndNew = async () => {
    await handleTaskUpsert(
      {
        ...values,
        content: isEmpty(values.content) ? '' : values.content
      },
      disableTaskCreation,
      session,
      newUserName,
      clientDetails,
      taskId,
      handleTaskCreated,
      pending,
      setValues,
      setPending,
      setEditSubtasks,
      editSubtasks,
      setChanged,
      () => { },
      startingVals,
      setLoadAdd,
      false,
      true,
      null,
      null,
      null,
      null,
      uploadUser
    )
    setNewUserName(null)
    scrollToTop()
    dispatch(AddSuccessAlert('Task created'))
  }

  const handleSubmitClick = async () => {
    try {
      await handleTaskUpsert(
        {
          ...values,
          content: isEmpty(values.content) ? '' : values.content
        },
        disableTaskCreation,
        session,
        newUserName,
        clientDetails,
        taskId,
        handleTaskCreated,
        pending,
        setValues,
        setPending,
        setEditSubtasks,
        editSubtasks,
        setChanged,
        () => { },
        startingVals,
        setLoadAdd,
        viewAfterCreate,
        true,
        null,
        null,
        null,
        null,
        uploadUser
      )
      dispatch(AddSuccessAlert('Changes saved'))
      onClose()
    } catch (err) {
      console.error('Error creating task', err)
      // alert has been already set.
    }
  }

  const handleTabSwitch = React.useCallback(
    tab => {
      const action = () => setTab(tab)

      if (!changed || tab === NOTES) {
        action()
        return
      }

      setCloseReason('You have unsaved changes')
      setCloseAction(action)
      setV2(true)
    },
    [changed, setCloseReason, setCloseAction, setV2]
  )

  const onDeleteClick = () => {
    setSubtext('')
    setTitle('Confirmation')
    setCloseReason('Are you sure you wish to delete this task?')
    setV2(true)
    setCloseAction(() => {
      removeTask()
    })
  }

  return (
    <BaseModal
      open={open}
      onClose={handleClose}
      title='TASK DETAILS'
      PaperProps={{
        style: {
          width: '65%',
          maxHeight: '90%'
        }
      }}
      disableEnforceFocus
      id={MODAL_ID}
      className={down451 && classes.mobile}
      {...props}
    >
      <div ref={topRef} />

      <div className={classes.parentTitle} onClick={goToParentTask}>
        {values.parent_name}
      </div>

      <div className={classes.nameSection}>
        <TaskArrows onChange={newID => setTaskOpen(newID)} modal />

        <div className={clsx(classes.textField, classes.padded)}>
          <input
            autoFocus
            placeholder='Give your task a name'
            value={values.title ?? ''}
            onChange={handleChange('title')}
            className={clsx(classes.input, 'text')}
            disabled={!canEdit}
            data-testid='task-name'
          />
        </div>

        {disableTaskCreation && (
          <HeaderButtons
            markComplete={markComplete}
            hideComplete={!showCompleteButton}
            disableComplete={
              !canComplete || isComplete || (!canEdit && !canUpdate)
            }
            canClose={canClose}
            hasAllSubtasksClosed={hasAllSubtasksClosed}
            title={
              canUpdate || canEdit
                ? null
                : "You don't have permission to close this task"
            }
            alreadyComplete={isComplete}
            taskID={taskId}
            hideGoto={!canViewTask}
            canEdit={canEdit}
            variant={'taskModal'}
            isClosed={Boolean(values.closed_at)}
            onClose={onClose}
          />
        )}
      </div>

      {down451 && (
        <>
          <div className={classes.bodyWrapper}>
            <div className={classes.body}>
              <div className={classes.description}>
                {canEdit && (
                  <ButtonMenu
                    options={detailsMenuOpts}
                    onSelect={handleDetailsMenuSelect}
                  >
                    <AddCircle
                      className={classes.buttonMenuIcon}
                      color={theme.palette.primary.main}
                      size={theme.size.base}
                    />
                  </ButtonMenu>
                )}
                DETAILS
              </div>
              <div className={classes.descriptionContainer}>
                <Editor
                  disableImages
                  maxLength={3000}
                  enabled={canEdit}
                  showSpeechToText={false}
                  placeholder={
                    canEdit
                      ? 'Enter task details'
                      : "You don't have permission to edit this task"
                  }
                  value={values.content ?? ''}
                  onChange={handleChange('content')}
                />
              </div>
            </div>
          </div>
          {canEdit && (
            <FileSection
              onMobile={down451}
              enablePastingFiles
              multi
              setValues={setValues}
              values={values}
              setChanged={setChanged}
              pasteSelector={`#${MODAL_ID}`}
            />
          )}
        </>
      )}

      <TaskDetails
        values={values}
        currentUser={currentUser}
        agents={agents}
        types={applyTaskCategoryRules(values.section, types)}
        categories={categories}
        statuses={statuses}
        priorities={priorities}
        handleChange={handleChange}
        searchUser={searchUser}
        sendValues={sendValues}
        canComplete={canComplete}
        canEdit={canEdit}
        canClose={canClose}
        setNewUserName={setNewUserName}
        newUserName={newUserName}
        canSearchUser={canSearchUser}
        canAddClient={canAddClient}
        expanded={expanded}
        setExpanded={setExpanded}
        showExpand
        taskId={taskId}
        modal
        teams={teams}
        isSubTask={isSubTask}
      />

      {expanded && (
        <>
          <TaskMetaSection
            taskID={taskId}
            reset={resetMeta}
            updateParentMeta={setMeta}
            values={values}
          />

          {!isSubTask && (
            <div className={classes.bodyWrapper}>
              <div className={classes.body}>
                <div className={classes.description}>TAGS</div>

                <TaskTagsSection
                  modal
                  canEdit={canEdit}
                  handleFields={handleFields}
                  values={values}
                />
              </div>
            </div>
          )}

          {up450 && (
            <>
              <div className={classes.bodyWrapper}>
                <div
                  className={classes.body}
                  style={{
                    position: 'relative'
                  }}
                >
                  <div className={classes.description}>
                    {canEdit && (
                      <ButtonMenu
                        options={detailsMenuOpts}
                        onSelect={handleDetailsMenuSelect}
                      >
                        <AddCircle
                          className={classes.buttonMenuIcon}
                          color={theme.palette.primary.main}
                          size={theme.size.base}
                        />
                      </ButtonMenu>
                    )}
                  </div>

                  <Tabs
                    value={tab}
                    onChange={(_, val) => handleTabSwitch(val)}
                    className={classes.tabs}
                    centered
                  >
                    <Tab label='Description' classes={{ root: classes.tab }} />

                    <Tab label='Notes' classes={{ root: classes.tab }} />

                    <Tab label='Emails' classes={{ root: classes.tab }} />
                  </Tabs>

                  <div className={classes.descriptionContainer}>
                    {tab === DESCRIPTION && (
                      <Editor
                        disableImages
                        maxLength={3000}
                        enabled={canEdit}
                        showSpeechToText={false}
                        value={values.content ?? ''}
                        onChange={handleChange('content')}
                        placeholder={
                          canEdit
                            ? 'Enter task details'
                            : "You don't have permission to edit this task"
                        }
                        className={classes.editor}
                      />
                    )}

                    {tab === NOTES && (
                      <TaskNote
                        values={values}
                        setValues={setValues}
                        setChanged={setChanged}
                        triggerSave={() => setChanged(true)}
                        inModal
                      />
                    )}

                    {tab === EMAILS && (
                      <EmailSection
                        values={values}
                        setValues={setValues}
                        triggerSave={() => setChanged(true)}
                      />
                    )}
                  </div>
                </div>
              </div>

              {canEdit && (
                <FileSection
                  enablePastingFiles
                  multi
                  setValues={setValues}
                  values={values}
                  setChanged={setChanged}
                  pasteSelector={`#${MODAL_ID}`}
                />
              )}
            </>
          )}

          {up1024 && !isSubTask && (
            <div className={classes.bodyWrapper}>
              <div className={classes.body}>
                <div
                  style={{
                    marginBottom: '1rem',
                    height: '1rem',
                    cursor: 'default'
                  }}
                >
                  <Hierarchy
                    size={theme.size.base}
                    variant='Bold'
                    color='grey'
                    className={classes.icon}
                  />{' '}
                  SUBTASKS
                </div>

                {existingSubtasks?.length > 0 &&
                  existingSubtasks.map((subtask, i) => (
                    <div
                      key={i}
                      className={classes.subtask}
                      style={{
                        color: pending.includes(subtask.id)
                          ? 'lightgrey'
                          : 'black'
                      }}
                    >
                      <Tooltip
                        title={
                          subtask.closed_at
                            ? `Closed on ${formatDateRelative(
                              subtask.closed_at,
                              true
                            )}`
                            : null
                        }
                        arrow
                      >
                        <span
                          style={{
                            paddingRight: '1rem',
                            cursor: 'pointer',

                            textDecoration: subtask.closed_at
                              ? 'line-through'
                              : 'none'
                          }}
                          onClick={() => subtaskRedirect(subtask)}
                        >
                          {subtask.title}
                        </span>
                      </Tooltip>

                      {canEdit && (
                        <CloseCircle
                          size={theme.size.base}
                          onClick={() => canEdit && pendingDelete(subtask)}
                          style={{ cursor: 'pointer' }}
                        />
                      )}
                    </div>
                  ))}

                {values.subtasks.map((subtask, i) => {
                  return (
                    <div
                      key={i}
                      className={classes.subtask}
                      onClick={() =>
                        canEdit && handleFields(subtask, 'subtasks', i)
                      }
                    >
                      {subtask}

                      <CloseCircle
                        size={theme.size.base}
                        style={{ marginLeft: '1rem' }}
                      />
                    </div>
                  )
                })}

                {showInput && canEdit ? (
                  <div
                    className={classes.input}
                    style={{ width: '50%', marginRight: '0.5rem' }}
                  >
                    <TextField
                      autoFocus
                      placeholder='Add subtask'
                      value={subtaskTitle || ''}
                      onChange={e => setSubtaskTitle(e.target.value)}
                      className={classes.transparent}
                      onKeyDown={e => handleFields(e, 'subtasks')}
                      disabled={!canEdit}
                    />
                  </div>
                ) : canEdit ? (
                  <div
                    onClick={() => setShowInput(true)}
                    style={{
                      color: 'grey',
                      cursor: canEdit ? 'pointer' : 'default',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <AddCircle
                      size={theme.size.base}
                      style={{ color: 'grey', marginRight: '0.5rem' }}
                    />
                    Add Subtask
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </>
      )}

      {canEdit && (
        <>
          <EndControls
            onSubmit={handleSubmitClick}
            submitText={`${isCreatingTask ? 'CREATE' : 'UPDATE'} TASK`}
            SubmitIcon={ticketIcon}
            showExtraIcons={!isCreatingTask}
            iconLeft
            onDelete={() => onDeleteClick()}
            disabled={disable || (!canEdit && !canUpdate)}
            hideDelete={subtasks?.length > 1 || !canDelete}
            smallMedia={down451}
            variant={'taskModal'}
          >
            {isCreatingTask && !down451 && !singleCreate && (
              <Button
                className={classes.extraBtn}
                disabled={disable || (!canEdit && !canUpdate)}
                onClick={handleAddAndNew}
              >
                {up450 && <ClipboardTick className={classes.buttonIcon} />}
                <span>ADD &amp; NEW</span>
              </Button>
            )}
          </EndControls>
          {isCreatingCddTask && (
            <div className={classes.addsubtasksRow}>
              <Button
                className={classes.cannedButton}
                onClick={addCddQuickTasks}
              >
                <ThunderIcon />
              </Button>

              <Info
                text={
                  <div>
                    Add the following subtasks to this new task:
                    <ul>
                      <li>Verify if customer payment history is current</li>
                      <li>Prepare Email Draft for Attorney</li>
                      <li>Send an Email to Attorney</li>
                      <li>
                        Identify if there are additional charges and collected
                      </li>
                      <li>Send Payment to Attorney</li>
                      <li>
                        Receive Ticket result From Attorney and Notify Customer
                      </li>
                    </ul>
                    The due date for these subtasks will be 7 weekdays before
                    the due date of the parent task
                  </div>
                }
              />
            </div>
          )}
        </>
      )}
    </BaseModal>
  )
}

export default TaskModal
